import CloseIcon from '@mui/icons-material/Close';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FileRejection, useDropzone } from 'react-dropzone';
import Box from '@mui/material/Box';

import Button from './Button';
import Typography from '@mui/material/Typography';

interface FileWithUrl extends File {
  url?: string;
}

// Supported file type mapping
const MIME_MAP: Record<string, string[]> = {
  excel: [
    'application/vnd.ms-excel', // .xls
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  ],
};

interface FileUploaderProps {
  value: FileWithUrl[];
  onChange: (_files: FileWithUrl[]) => void;
  error?: boolean;
  setError?: (_name: string, _error: { type: string; message: string }) => void;
  clearErrors?: (_name: string) => void;
  required?: boolean;
  enableBrowseBtn?: boolean;
  accept?: string[];
  maxSizeMB?: number;
  previewUrl?: string;
  name?: string;
  errorMessage?: string;
}

const FileUploader: React.FC<FileUploaderProps> = ({
  value,
  onChange,
  error,
  setError,
  clearErrors,
  required = false,
  enableBrowseBtn = false,
  accept = ['excel'], // only Excel files allowed
  maxSizeMB = 3,
  previewUrl,
  name = 'file',
  errorMessage = '',
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  // Build accepted MIME types from accept prop
  const acceptedMimesArr = accept.flatMap((type) => MIME_MAP[type] || []);
  const acceptMimes = acceptedMimesArr.reduce<Record<string, string[]>>(
    (acc, mime) => ({ ...acc, [mime]: [] }),
    {},
  );

  // Drop handler
  const onDrop = useCallback(
    (acceptedFiles: FileWithUrl[], rejectedFiles: FileRejection[]) => {
      if (rejectedFiles.length > 0) {
        let errMsg: string;
        const code = rejectedFiles[0].errors[0].code;
        if (code === 'file-invalid-type') errMsg = t('common.acceptedFormatsExcel');
        else errMsg = t('common.fileSizeMax', { max: maxSizeMB });

        setError && setError(name, { type: 'manual', message: errMsg });
        onChange([]); // clear files
        return;
      }
      clearErrors && clearErrors(name);
      onChange(acceptedFiles);
    },
    [onChange, setError, clearErrors, maxSizeMB, name, t],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: acceptMimes,
    maxSize: maxSizeMB * 1024 * 1024,
    multiple: false,
    noClick: !enableBrowseBtn,
    noKeyboard: false,
  });

  // Remove the selected file
  const handleRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onChange([]);
    if (required) {
      setError && setError(name, { type: 'manual', message: t('common.fileRequired') });
    }
  };

  // Styles from theme
  const borderColor = error
    ? theme.palette.fileUploader.borderError
    : isDragActive
      ? theme.palette.fileUploader.borderActive
      : theme.palette.fileUploader.borderDefault;
  const hoverBorderColor = error
    ? theme.palette.fileUploader.hoverError
    : theme.palette.fileUploader.hoverActive;

  return (
    <>
      {value && value.length > 0 && value[0] ? (
        <Box
          sx={{
            border: `2px solid ${borderColor}`,
            borderRadius: `${theme.borderRadius.md}px`,
            py: 2,
            textAlign: 'center',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
            transition: `border-color ${theme.transitions.custom.fast}`,
            overflow: 'hidden',
          }}
        >
          <IconButton
            size="small"
            onClick={handleRemove}
            sx={{
              position: 'absolute',
              right: 10,
              top: 10,
              color: theme.palette.fileUploader.borderError,
            }}
            aria-label={t('common.removeFile')}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          {/* Excel file icon + filename on one line */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.3 }}>
            <img
              src="/excelIcon.png" // <-- place an Excel icon in your public folder
              alt={t('common.excelFile')}
              style={{
                height: 20,
                width: 20,
                objectFit: 'contain',
                borderRadius: 4,
              }}
            />
            <Typography
              component="span"
              sx={{
                color: theme.palette.fileUploader.filenameBlue,
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              {value[0]?.name ?? t('common.unknownFile')}
            </Typography>
          </Box>
          <Typography sx={{ color: theme.palette.fileUploader.textMuted, fontSize: 12.5 }}>
            {value[0]?.size ? (value[0].size / 1024 / 1024).toFixed(2) + ' MB' : '0 MB'}
          </Typography>
        </Box>
      ) : (
        <Box
          {...getRootProps()}
          sx={{
            border: `2px dashed ${borderColor}`,
            borderRadius: `${theme.borderRadius.md}px`,
            py: 2,
            textAlign: 'center',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            cursor: 'pointer',
            outline: 'none',
            transition: `border-color ${theme.transitions.custom.fast}`,
            overflow: 'hidden',
            ':hover': {
              borderColor: hoverBorderColor,
            },
          }}
          tabIndex={0}
          aria-label={t('common.excelFile')}
          onClick={(e) => {
            e.stopPropagation();
            open();
          }}
        >
          <input {...getInputProps()} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.6 }}>
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="preview"
                style={{
                  height: 24,
                  width: 24,
                  objectFit: 'cover',
                  borderRadius: 4,
                }}
              />
            ) : (
              <UploadFileOutlinedIcon
                sx={{ color: theme.palette.fileUploader.iconBlue, fontSize: 20 }}
              />
            )}
            <span
              style={{
                fontWeight: 600,
                color: theme.palette.fileUploader.uploadText,
                fontSize: 14,
              }}
            >
              {t('common.upload')}
            </span>
            <Typography
              component="span"
              sx={{ color: theme.palette.fileUploader.textLight, fontSize: 14 }}
            >
              {t('common.orDragAndDrop')}
            </Typography>
          </Box>
          <Typography sx={{ color: theme.palette.fileUploader.textMuted, fontSize: 12.5, mt: 0.6 }}>
            {t('common.excelMaxSize', { max: maxSizeMB })}
          </Typography>

          {enableBrowseBtn && (
            <Button
              variant="blue"
              sx={{
                fontWeight: 600,
                fontSize: 14,
                px: 2,
                mt: 2,
              }}
              onClick={(e: any) => {
                e.stopPropagation();
                open();
              }}
            >
              {t('common.browseFile')}
            </Button>
          )}
        </Box>
      )}
      {(Boolean(errorMessage) || Boolean(error)) && (
        <Typography
          sx={{
            color: theme.palette.fileUploader.borderError,
            fontSize: 12,
            mt: 1.2,
            ml: 0.5,
          }}
        >
          {errorMessage || t('common.invalidFile')}
        </Typography>
      )}
    </>
  );
};

export default FileUploader;
